import React from 'react';
import { Box, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, alpha, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Statement, StatementLine } from '../../../types/finance';
import { formatStatementPaisa } from '../../../utils/paisa';
import { ledgerPath } from '../ledger/ledgerPath';


/**
 * A financial statement: headings, indented groups and accounts, subtotals
 * and totals, one amount column per period. Negatives in brackets; each
 * account figure opens that account's ledger over the column's dates.
 */
const StatementTable: React.FC<{ statement: Statement }> = ({ statement }) => {
  const theme = useTheme();
  const { columns } = statement;

  const amountCell = (line: StatementLine, i: number) => {
    const paisa = line.amounts?.[i] ?? 0;
    const text = paisa === 0 ? '—' : formatStatementPaisa(paisa);
    const strong = line.kind === 'total' || line.kind === 'subtotal' || line.kind === 'group';
    const body = (
      <Typography variant="body2" component="span" fontWeight={line.kind === 'total' ? 800 : strong ? 700 : 400}
        color={paisa === 0 ? 'text.disabled' : paisa < 0 ? 'error.main' : 'text.primary'}
        sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        {text}
      </Typography>
    );
    return (
      <TableCell key={i} align="right" sx={{ width: 170 }}>
        {line.kind === 'account' && line.accountId && paisa !== 0
          ? <Link component={RouterLink} to={ledgerPath(line.accountId!, columns[i])} underline="hover" color="inherit">{body}</Link>
          : body}
      </TableCell>
    );
  };

  const row = (line: StatementLine, index: number) => {
    if (line.kind === 'heading') {
      return (
        <TableRow key={index}>
          <TableCell colSpan={columns.length + 1} sx={{ pt: 2.5, pb: 1, borderBottom: `1.5px solid ${theme.palette.divider}` }}>
            <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
              {line.label}
            </Typography>
          </TableCell>
        </TableRow>
      );
    }
    if (line.kind === 'note') {
      return (
        <TableRow key={index}>
          <TableCell colSpan={columns.length + 1} sx={{ pl: 2 + line.depth * 2.5, color: 'text.secondary', fontStyle: 'italic' }}>
            <Typography variant="body2" color="text.secondary" fontStyle="italic">{line.label}</Typography>
          </TableCell>
        </TableRow>
      );
    }
    const isTotal = line.kind === 'total';
    const isSubtotal = line.kind === 'subtotal';
    return (
      <TableRow
        key={index}
        sx={{
          ...(isSubtotal && { bgcolor: alpha(theme.palette.text.primary, 0.025), '& td': { borderTop: `1px solid ${theme.palette.divider}` } }),
          ...(isTotal && { bgcolor: alpha(theme.palette.primary.main, 0.05), '& td': { borderTop: `2px solid ${theme.palette.text.primary}`, borderBottom: `3px double ${theme.palette.text.primary}` } }),
        }}
      >
        <TableCell sx={{ pl: 2 + line.depth * 2.5 }}>
          <Typography variant="body2" fontWeight={isTotal ? 800 : isSubtotal || line.kind === 'group' ? 700 : 400}>
            {line.code && <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.secondary', mr: 1 }}>{line.code}</Box>}
            {line.label}
          </Typography>
        </TableCell>
        {columns.map((_, i) => amountCell(line, i))}
      </TableRow>
    );
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
              <TableCell sx={{ py: 1.5 }}><Typography variant="body2" fontWeight={700}>{statement.title}</Typography></TableCell>
              {columns.map(c => (
                <TableCell key={c.label} align="right" sx={{ py: 1.5 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{c.label}</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>{statement.lines.map(row)}</TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default StatementTable;
